import { Button } from "@pepperstone/pepper-ui-kit";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  SavedPaymentAccountIdAndFields,
  Error as SdkError,
} from "@/api/cashier";
import { DeleteConfirmation } from "@/components/delete-saved-payment-dialog/delete-confirmation/DeleteConfirmation";
import { DeleteForm } from "@/components/delete-saved-payment-dialog/delete-form/DeleteForm";
import { processDeleteRequest } from "@/components/delete-saved-payment-dialog/processDeleteRequest";
import {
  type DeleteFormData,
  INITIAL_FORM_DATA,
} from "@/components/delete-saved-payment-dialog/types";
import { ErrorMessage } from "@/components/ErrorMessage";
import { BaseModal } from "@/components/general/base-modal/BaseModal";
import { ProgressSpinner } from "@/components/progress-spinner/ProgressSpinner";
import { notify } from "@/components/toast/notify.ts";
import { useLogger } from "@/hooks/useLogger";
import { serializeError } from "@/utils/log";

/**
 * Translation key declaration for i18next-parser extraction.
 * This key is declared statically so the parser can detect it even if it is not
 * referenced in a way that static analysis can discover automatically.
 * This code never executes but is required for the build process.
 */
// t("deposit.saved_payment_account.deletion_request_cannot_be_created");

type DeleteSavedPaymentDialogProps = {
  onCancel: () => void;
  onConfirm: () => void;
  isOpen: boolean;
  selectedSavedPaymentAccount: SavedPaymentAccountIdAndFields;
};

export const DeleteSavedPaymentDialog = ({
  selectedSavedPaymentAccount,
  isOpen,
  onCancel,
  onConfirm,
}: DeleteSavedPaymentDialogProps) => {
  const { t } = useTranslation();
  const logger = useLogger();
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<DeleteFormData>(INITIAL_FORM_DATA);
  const [showConfirmStep, setShowConfirmStep] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const {
    mutate: processDeleteRequestMutate,
    isPending,
    error: uploadError,
  } = useMutation({
    mutationFn: processDeleteRequest,
    onError: (e: SdkError) => {
      logger.error("Failed to delete saved payment method", {
        error: serializeError(e),
      });
    },
    onSuccess: () => {
      formRef.current?.reset();
      onConfirm();
      notify.success(t("global.notifications.payment_method_removed"));
      setShowConfirmStep(false);
      setFormData(INITIAL_FORM_DATA);
    },
  });

  const isSubmitDisabled =
    formData.reason?.state === "invalid" ||
    formData.file?.state === "invalid" ||
    formData.reason?.state === "pristine" ||
    isPending;
  const isCancelDisabled = isPending;

  const handleSubmit = () => {
    if (!isSubmitDisabled) {
      if (!showConfirmStep) {
        setShowConfirmStep(true);
        setFormData(formData);
      } else {
        if (formData.reason?.value && selectedSavedPaymentAccount?.id) {
          processDeleteRequestMutate({
            formData,
            savedPaymentAccountId: selectedSavedPaymentAccount.id,
            onProgress: setUploadProgress,
          });
        }
      }
    }
  };
  const handleCancel = () => {
    if (!isCancelDisabled) {
      if (!showConfirmStep) {
        setFormData(INITIAL_FORM_DATA);
        onCancel();
      } else {
        setShowConfirmStep(false);
      }
    }
  };

  return (
    <BaseModal
      onCancel={handleCancel}
      isCancelable={!isCancelDisabled}
      style={{ width: "min(550px, 90%)" }}
      isOpen={isOpen}
      onClose={handleCancel}
      testId={"delete-saved-payment-dialog"}
    >
      <div>
        <h3 style={{ fontSize: "24px", fontWeight: 700 }}>
          {!showConfirmStep
            ? t("deposit.methods.saved_methods.remove_prompt")
            : t("deposit.methods.saved_methods.confirm_removal")}
        </h3>
        {/* main content */}
        {!showConfirmStep ? (
          <DeleteForm
            ref={formRef}
            formData={formData}
            setFormData={setFormData}
          />
        ) : (
          <DeleteConfirmation formData={formData} />
        )}
        {/* status indicators */}
        <div>
          {uploadError && (
            <ErrorMessage
              message={
                t(uploadError.key) ||
                t("deposit.methods.saved_methods.upload_error_title")
              }
            />
          )}
          {isPending && <ProgressSpinner progress={uploadProgress} />}
        </div>
      </div>
      {/* footer */}
      <div className="ps-flex ps-gap-2">
        <div style={{ marginInlineStart: "auto" }} />
        <Button
          disabled={isCancelDisabled}
          data-testid={"delete-saved-payment-dialog-cancel-button"}
          variant="ps-btn-secondary"
          type="button"
          onClick={handleCancel}
          className="ps-ml-full"
        >
          {!showConfirmStep
            ? t("deposit.methods.saved_methods.cancel")
            : t("deposit.methods.saved_methods.back")}
        </Button>
        <Button
          isLoading={isPending}
          disabled={isSubmitDisabled}
          data-testid={"delete-saved-payment-dialog-delete-button"}
          variant={!showConfirmStep ? "ps-btn-primary" : "ps-btn-destructive"}
          type="button"
          onClick={handleSubmit}
          className="ps-ml-full"
        >
          {!showConfirmStep
            ? t("deposit.methods.saved_methods.delete")
            : t("deposit.methods.saved_methods.confirm")}
        </Button>
      </div>
    </BaseModal>
  );
};
